import { useCallback, useEffect, useRef, useState } from "react";

import { sv } from "date-fns/locale";
// date picker
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";

import ReloadIcon from "@/assets/icons/relaod.svg";
import Button from "@/components/Button";
import Tooltip from "@components/Tooltip";
import { i18n } from "@lingui/core";

import { AESimpleDatepicker } from "./AESimpleDatepicker";
import DetailsModal, { ComponentErrors } from "./DetailsModal";
import Query from "./Query";

registerLocale("sv", sv);

import { getCapabilities } from "@/api/endpoints/organization";
import { refreshSearch } from "@/api/endpoints/searches";
import fetcher_v2 from "@/api/fetcher_v2";
import { URLS } from "@/api/urls";
import InformationCircle from "@/assets/icons/information-circle.svg";
import PlusIcon from "@/assets/icons/plus.svg";
import { SEARCH_TYPES, SearchStatus, Source } from "@/constants";
import ConfirmModal from "@/containers/ConfirmModal";
import { useMessages } from "@/containers/Messages/MessageContext";
import FromTemplate from "@/containers/QueryBuilder/FromTemplate";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { ROUTES } from "@/routes_new";
import { Episode } from "@/types/Episode";
import { formatDateForApi } from "@/utils";
import { sumArray } from "@/views/reports/widgets/helpers";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { Input, Select } from "@components/Field";
import Label from "@components/Field/Label";
import Spinner from "@components/Spinner";
import StaffBadge from "@components/StaffBadge";
import Typography from "@components/Typography";

import CreatedBy from "./CreatedBy";
import {
  FilterKey,
  prepareQuery,
  PROP_TO_PARAM_MAP,
  QueryIn,
  QueryOut,
  SnakeKey,
} from "./requestUtils";
import { UIQuery } from "./types";
import { validationErrors } from "./utils";

const optionTypeLabels = {
  [SEARCH_TYPES.BRAND]: L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_BRAND,
  [SEARCH_TYPES.INDUSTRY_OR_AREA_OF_INTEREST]:
    L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_INDUSTRY_OR_AREA_OF_INTEREST,
  [SEARCH_TYPES.COMPETITORS]: L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_COMPETITORS,
  [SEARCH_TYPES.CAMPAIGN_OR_PROJECT]:
    L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_CAMPAIGN_OR_PROJECT,
  [SEARCH_TYPES.KEY_PEOPLE]: L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_KEY_PEOPLE,
  [SEARCH_TYPES.OTHER]: L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_OTHER,
};

export type PreviewMeta = {
  estimated_mentions: number;
  total_matches: number;
  page_token: number | null;
};

type PreviewResponse = {
  meta: PreviewMeta;
  results: Episode[];
};

type SaveSearchResponse = {
  uuid: string;
  queries?: QueryOut[];
};

type SaveSearchBody = {
  title: string;
  subscriptions?: LoadedSearchSubscription[];
  sorting_position: string | number;
  search_type: string;
  start_date: string;
  end_date: string | null;
  queries: QueryIn[];
  industry: string | null;
};

export interface QueryBuilderProps {
  initializeEmptyForm?: boolean;
  onTitleChange?: (title: string) => void;
  subscriptions?: LoadedSearchSubscription[];
  onSave?: (args: { uuid: string }) => void;
  onClose?: () => void;
  setPreviewMetaResults: (meta: PreviewMeta | null) => void;
  setPreviewMentions: (rows: Episode[]) => void;
}

export interface OrganizationCapabilities {
  sources: Source[];
  languages: string[];
  industries: string[];
}

export interface IndexedQueryErrors {
  index: number;
  errors?: string[];
}

export type LoadedSearchSubscription = {
  email: string;
  full_name: string;
  id: number;
  interval: "none" | "realtime" | "daily" | "weekly";
};

export type LoadSearchResponse = {
  can_mentions_be_presented: boolean;
  created: string;
  created_by: string;
  description: string | null;
  end_date: string | null;
  id: number;
  industry: string | null;
  is_initializing: boolean;
  is_prioritized: boolean;
  kind: "report";
  last_modified: string;
  queries: QueryOut[];
  revision_url: string;
  search_type: string;
  sorting_position: number;
  start_date: string | null;
  status: SearchStatus;
  subscriptions: LoadedSearchSubscription[];
  title: string;
  uuid: string;
};

export const QueryBuilder: React.FC<QueryBuilderProps> = ({
  initializeEmptyForm,
  onTitleChange,
  ...props
}) => {
  const lastMessage = useRef<number>(undefined);
  const [openDropdown, setOpenDropdown] = useState<{
    filterName: string;
    index: number;
  } | null>(null);

  const [addedControls, setAddedControls] = useState<FilterKey[][]>([]);
  const [queries, setQueries] = useState<UIQuery[]>([]);

  const [uuid, setUuid] = useState<string>();
  const [url, setUrl] = useState<string>();
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();
  const [sortingPosition, setSortingPosition] = useState<string | number>(10);
  const [errors, setErrors] = useState<ComponentErrors>({});
  const [queryErrors, setQueryErrors] = useState<IndexedQueryErrors[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const { danger, success, clearMessage } = useMessages();

  const [isSaving, setIsSaving] = useState(false);
  const [previewButtonEnabled, setPreviewButtonEnabled] = useState(false);
  const [previewIsLoading, setPreviewIsLoading] = useState(false);
  const [saveButtonEnabled, setSaveButtonEnabled] = useState(true);
  const [previewErrors, setPreviewErrors] = useState<string | undefined>();
  const [searchDetailsModalVisible, setSearchDetailsModalVisible] =
    useState(false);
  const [refreshAlertModalOpen, setRefreshAlertModalOpen] = useState(false);

  const [sources, setSources] = useState<Source[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [lastQueryParams, setLastQueryParams] = useState<Record<
    string,
    unknown
  > | null>();
  const [searchType, setSearchType] = useState("");
  const [selectableIndustries, setSelectableIndustries] = useState<string[]>(
    []
  );
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>("");

  const { data: user } = useUser();
  const {
    is_staff = false,
    is_readonly = true,
    language = "en",
  } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { uuid: organization_uuid } = orgData?.ok ? orgData.data : {};
  const history = useHistory();

  const isCreateView = useRouteMatch(ROUTES.MENTION_NEW_ORGID)?.isExact;

  const refreshAlert = () => setRefreshAlertModalOpen(true);

  const getConfirmRefreshAlertMessage = () =>
    L.MENTIONS_WILL_REMOVE +
    ` ${L.MENTIONS_ALL_EXISTING} ${L.MENTIONS_PLURAL}. ` +
    L.MENTIONS_CONFIRM;

  const confirmRefreshAlert = () => {
    if (!uuid) return;
    refreshSearch(uuid);
    history.push(
      generatePath(ROUTES.MENTION_DETAIL_ORGID, {
        uuid,
        orgUuid: organization_uuid,
      })
    );
    window.location.reload();
  };

  const closeRefreshAlertModal = () => setRefreshAlertModalOpen(false);

  const onAddQuery = useCallback(() => {
    // The sources and languages controls are always shown, and
    // given all available values as default
    setQueries(
      queries.concat([
        {
          sources: sources,
          languages: languages,
          nearDistance: 10,
          notNearDistance: 10,
        },
      ])
    );
    setAddedControls(
      addedControls.concat([
        ["languages", "sources", "nearKeywords", "notNearKeywords"],
      ])
    );
  }, [addedControls, languages, queries, sources]);

  useEffect(() => {
    // If creating new alert, wait for capabilities to load and auto-add one query
    if (
      user?.ok &&
      queries.length === 0 &&
      initializeEmptyForm &&
      sources.length > 0 &&
      languages.length > 0
    ) {
      onAddQuery();
    }
  }, [
    user?.ok,
    orgData?.ok,
    queries,
    sources,
    onAddQuery,
    initializeEmptyForm,
    languages,
  ]);

  useEffect(() => {
    title && onTitleChange?.(title);
  }, [title, onTitleChange]);

  const activeUrlUuid = useRouteMatch<{ uuid: string }>(
    ROUTES.MENTION_SETTINGS_ORGID
  )?.params?.uuid;
  const archiveUrlUuid = useRouteMatch<{ uuid: string }>(
    ROUTES.ARCHIVE_SETTINGS_ORGID
  )?.params?.uuid;
  useEffect(() => {
    const urlUuid = activeUrlUuid || archiveUrlUuid;
    if (urlUuid) {
      setUuid(urlUuid);
      setUrl(generatePath(URLS.SEARCHES_DETAIL, { uuid: urlUuid }));
    }
  }, [activeUrlUuid, archiveUrlUuid]);

  useEffect(() => {
    if (!organization_uuid) return;
    // set date picker language to user's
    setDefaultLocale(language);

    // load the organization's capabilities
    getCapabilities(organization_uuid)
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.data as OrganizationCapabilities;
      })
      .then((data) => {
        setSources(data.sources);
        setLanguages(data.languages);
        setSelectableIndustries(data.industries);
      })
      .catch((error) => {
        document.body.innerHTML = `<div class="error" style="padding: 1rem; text-align: center">${String(
          error
        )}`;
        throw error;
      });
  }, [language, organization_uuid]);

  const setErrorMessages = (newErrors: IndexedQueryErrors[]) => {
    let hasLanguagesErrors = false;
    let hasSourcesErrors = false;
    for (const error of newErrors) {
      if (error.errors?.includes("languages")) hasLanguagesErrors = true;
      if (error.errors?.includes("sources")) hasSourcesErrors = true;
    }

    const errorStrings = [];
    if (hasLanguagesErrors) {
      errorStrings.push({
        error:
          L.MENTIONS_QUERYBUILDER_REQUIRED_QUERY_LANGUAGES_FILTER_EMPTY_ERROR,
      });
    }
    if (hasSourcesErrors) {
      errorStrings.push({
        error:
          L.MENTIONS_QUERYBUILDER_REQUIRED_QUERY_SOURCES_FILTER_EMPTY_ERROR,
      });
    }
    setErrors({
      queries: errorStrings,
    });
  };

  const addQueryError = (error: string, queryIndex: number) => {
    const errors = queryErrors.find((e) => e.index === queryIndex);

    const newErrors = [
      ...queryErrors.filter((e) => e.index !== queryIndex),
      {
        index: queryIndex,
        errors: errors?.errors ? errors.errors.concat(error) : [error],
      },
    ];
    setQueryErrors(newErrors);
    setErrorMessages(newErrors);
  };

  const clearQueryError = (error: string, queryIndex: number) => {
    const errors = queryErrors.find((e) => e.index === queryIndex);
    const newErrors = [
      ...queryErrors.filter((e) => e.index !== queryIndex),
      {
        index: queryIndex,
        errors: errors?.errors?.filter((e) => e !== error),
      },
    ];
    setQueryErrors(newErrors);
    setErrorMessages(newErrors);
  };

  const hasQueryErrors = (newErrors = null) => {
    for (const e of newErrors !== null ? newErrors : queryErrors) {
      if (e.errors?.length) {
        return true;
      }
    }
    return false;
  };

  const controlShouldBeAdded = useCallback(
    (controlName: SnakeKey, value: unknown) => {
      // keywords is not a regular control, it is always shown
      const excludedControlNames: SnakeKey[] = [
        PROP_TO_PARAM_MAP["keywords"],
        PROP_TO_PARAM_MAP["nearDistance"],
        PROP_TO_PARAM_MAP["notNearDistance"],
      ];
      if (excludedControlNames.includes(controlName)) return false;

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (controlName === "min_reach_by_channel_type") {
        // Hide by default and only show if custom values are used
        if (
          value != null &&
          typeof value === "object" &&
          "has_custom_values" in value
        ) {
          return value["has_custom_values"];
        } else {
          return false;
        }
      }

      // Control for mention_types is handled as unset when its value is all
      if (controlName === "mention_types" && value === "all") {
        return false;
      }

      // Control for include_paywalled is handled as unset when its value is false
      if (controlName === "include_paywalled" && value === false) {
        return false;
      }

      return true;
    },
    []
  );

  const getQueriesWithOptions = (queries: QueryOut[]): UIQuery[] => {
    const filters = Object.keys(PROP_TO_PARAM_MAP) as FilterKey[];

    return queries.map((query) => {
      const q = filters.reduce(
        (all, each) => {
          all[each] = query[PROP_TO_PARAM_MAP[each]];
          return all;
        },
        {} as Record<FilterKey, unknown>
      );

      return {
        ...q,
        options: { ...q },
      } as UIQuery;
    });
  };

  const loadSearch = useCallback(() => {
    if (!url) return;
    setSaveButtonEnabled(false);

    fetcher_v2<LoadSearchResponse>(url)
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.data;
      })
      .then((data) => {
        const filters = Object.keys(PROP_TO_PARAM_MAP) as FilterKey[];

        const controls = data.queries.map((query) => {
          return filters.filter((v) => {
            const param = PROP_TO_PARAM_MAP[v];
            return controlShouldBeAdded(param, query[param]);
          });
        });

        for (const index in controls) {
          if (!controls[index].includes("nearKeywords")) {
            controls[index] = controls[index].concat("nearKeywords");
          }
          if (!controls[index].includes("notNearKeywords")) {
            controls[index] = controls[index].concat("notNearKeywords");
          }
        }

        setAddedControls(controls);

        // fill query options from values
        const queriesWithOptions = data?.queries
          ? getQueriesWithOptions(data.queries)
          : [];

        setQueries(queriesWithOptions);
        setUuid(data.uuid);
        setTitle(data.title);
        setSortingPosition(data.sorting_position);
        setCreatedBy(data.created_by);
        setCreated(data.created);
        if (data.start_date) {
          setStartDate(new Date(data.start_date));
        }
        if (data.end_date) {
          setEndDate(new Date(data.end_date));
        }
        setSearchType(data.search_type);
        setSelectedIndustry(data.industry);

        // enable buttons
        setIsLoaded(true);
        setSaveButtonEnabled(true);
      });
  }, [controlShouldBeAdded, url]);

  useEffect(() => {
    if (url) loadSearch();
  }, [url, loadSearch]);
  const copySettingsQuery = (index: number) => {
    const query = queries[index] || null;
    const addedQueryControls = Array.from(addedControls[index]);

    const tagsControlIndex = addedQueryControls.indexOf("tags");
    if (tagsControlIndex !== -1) {
      addedQueryControls.splice(tagsControlIndex, 1);
    }

    if (!query || !addedControls) {
      setErrors({
        non_field_errors: [L.MENTIONS_QUERYBUILDER_COPY_SETTINGS_FAILED],
      });
      return;
    }

    setQueries(
      queries.concat([
        {
          ...query,
          options: {
            ...query.options,
            uuid: undefined,
            keywords: [],
            nearKeywords: [],
            notNearKeywords: [],
            tags: [],
          },
          uuid: null,
          keywords: [],
          nearKeywords: [],
          notNearKeywords: [],
          tags: [],
        },
      ])
    );

    setAddedControls(addedControls.concat([addedQueryControls]));
  };

  const getParamsForAPI = () => {
    const naiveStartDate = startDate
      ? formatDateForApi(new Date(startDate))
      : formatDateForApi(new Date());
    const naiveEndDate = endDate ? formatDateForApi(new Date(endDate)) : null;

    // assume new object unless url
    return {
      start_date: naiveStartDate,
      end_date: naiveEndDate,
      queries: (queries as QueryIn[]).map(prepareQuery),
      industry: selectedIndustry,
    };
  };

  const setTitleAndDetails = () => setSearchDetailsModalVisible(true);
  const hideSearchDetailsModal = () => setSearchDetailsModalVisible(false);

  const saveSearch = () => {
    setIsSaving(true);

    // assume new object unless url
    const data = {
      ...getParamsForAPI(),
      title,
      subscriptions: props.subscriptions,
      sorting_position: sortingPosition,
      search_type: searchType,
    };

    // We should make the PUT here into a PATCH so that we don't have to pass along the subscriptions.
    // They are somewhat separate.

    fetcher_v2<SaveSearchResponse, SaveSearchBody>(url || URLS.SEARCHES_LIST, {
      method: uuid ? "PUT" : "POST",
      body: data,
    })
      .then((response) => {
        if (response.ok) {
          return response.data;
        } else {
          return Promise.reject(response);
        }
      })
      .then(async (data) => {
        const didCreate = !uuid;

        setUuid(data.uuid);

        // fill query options from values
        const queriesWithOptions = data?.queries
          ? getQueriesWithOptions(data.queries)
          : [];

        setQueries(queriesWithOptions);

        setErrors({});
        setIsSaving(false);

        props.onSave?.({ uuid: data.uuid });
        props.onClose?.();

        if (lastMessage.current) clearMessage(lastMessage.current);
        lastMessage.current = await success({
          title: didCreate
            ? L.MENTIONS_QUERYBUILDER_CREATE_SUCCESSFUL
            : L.MENTIONS_QUERYBUILDER_SAVE_SUCCESSFUL,
        });
      })
      .catch(async (err) => {
        // Handle validation errors
        if (err.status === 400) {
          setErrors(err.data);
        }

        const isServerError = err.status >= 500;

        if (!isServerError) {
          const errors = validationErrors(err.data);

          // clear the last message we showed.
          if (lastMessage.current) clearMessage(lastMessage.current);

          lastMessage.current = await danger({
            title: L.MENTIONS_QUERYBUILDER_SAVE_FAILED,
            duration: 5000,
            message:
              errors.length > 0
                ? L.MENTIONS_QUERYBUILDER_SAVE_FAILED_FAILING_FIELDS +
                  "\n" +
                  errors.join("\n")
                : L.MENTIONS_QUERYBUILDER_SAVE_FAILED_500,
          });
        }

        const onlyQueryErrors =
          Object.keys(err.data).length === 1 && err.data["queries"];

        // scroll to top if there are validation errors except in queries
        if (!onlyQueryErrors) {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }

        setIsSaving(false);
      });
  };

  useEffect(() => {
    if (isCreateView) {
      const hasAnyKeywords =
        queries.map((q) => (q.keywords || []).length).reduce(sumArray, 0) > 0;
      setPreviewButtonEnabled(hasAnyKeywords);
      setSaveButtonEnabled(false);
    }
  }, [queries, isCreateView]);

  const onSuccessfulPreviewFetch = (data: PreviewResponse) => {
    setPreviewIsLoading(false);
    setSaveButtonEnabled(true);
    props.setPreviewMetaResults(data.meta);
    props.setPreviewMentions(data.results);
  };

  const onFailedPreviewFetch = (errorText: string) => {
    if (errorText === L.MENTIONS_QUERYBUILDER_PREVIEW_TIMEOUT_ERROR) {
      setSaveButtonEnabled(true);
    }
    setPreviewErrors(errorText);
    setPreviewIsLoading(false);
    props.setPreviewMetaResults(null);
    props.setPreviewMentions([]);
    setLastQueryParams(null);
  };

  const showPreview = () => {
    const params = getParamsForAPI();

    if (JSON.stringify(params) === JSON.stringify(lastQueryParams)) {
      return;
    }

    setPreviewIsLoading(true);
    props.setPreviewMetaResults(null);
    props.setPreviewMentions([]);
    setPreviewErrors(undefined);

    setLastQueryParams(params);

    const data = {
      ...params,
    };

    let timer: ReturnType<typeof setTimeout> | undefined;

    // takes a promise and rejects it if the timeout is reached
    const timeout = (promise: Promise<PreviewResponse>, time: number) =>
      Promise.race([
        promise,
        new Promise((_, reject) => {
          timer = setTimeout(
            () =>
              reject(new Error(L.MENTIONS_QUERYBUILDER_PREVIEW_TIMEOUT_ERROR)),
            time
          );
        }),
      ]);

    type PreviewBody = {
      start_date: string | null;
      end_date: string | null;
      queries: QueryOut[];
      industry: string | null;
    };

    // make non-ok responses from fetch reject the promise
    const fetchPromise = new Promise<PreviewResponse>((resolve, reject) => {
      fetcher_v2<PreviewResponse, PreviewBody, ComponentErrors>(
        URLS.API_PREVIEW_RESULTS,
        {
          method: "POST",
          body: data,
        }
      ).then((response) => {
        if (response.ok) {
          resolve(response.data);
        } else {
          if (response.status === 400) {
            setErrors(response.data as ComponentErrors);
            reject(new Error());
          } else {
            reject(new Error(L.MENTIONS_QUERYBUILDER_PREVIEW_GENERIC_ERROR));
          }
        }
      });
    });

    const previewFetchTimeout = 60 * 1000;

    timeout(fetchPromise, previewFetchTimeout)
      .then(
        // fetch is successful
        (data) => onSuccessfulPreviewFetch(data as PreviewResponse),

        // failing promise when response had errors
        (error) => onFailedPreviewFetch(error.message)
      )
      .finally(() => {
        clearTimeout(timer);
      });
  };

  const removeKeywordFromQuery = (
    keyword: string,
    key: "keywords" | "nearKeywords" | "nearNearKeywords" | "notNearKeywords",
    index: number
  ) => {
    const q = [...queries];
    q[index][key] = (q[index][key] || []).filter((k) => k !== keyword);
    if (q[index]["options"]?.[key]) {
      q[index]["options"][key] = q[index]["options"][key].filter(
        (k) => k !== keyword
      );
    }
    setQueries(q);
  };

  const onDetailsModalClose = () => setSearchDetailsModalVisible(false);

  return (
    <>
      {isCreateView && (
        <AccessControl level={ACCESS_LEVELS.STAFF}>
          <FromTemplate
            onSet={(templateQueries) => {
              // This is straight up taken from what we're doing when we're fetching an
              // existing search.
              const filters = Object.keys(PROP_TO_PARAM_MAP) as FilterKey[];

              const controls = templateQueries.map((query) => {
                return filters.filter((v) => {
                  const param = PROP_TO_PARAM_MAP[v];
                  return controlShouldBeAdded(param, query[param]);
                });
              });

              for (const index in controls) {
                if (!controls[index].includes("nearKeywords")) {
                  controls[index] = controls[index].concat("nearKeywords");
                }
                if (!controls[index].includes("notNearKeywords")) {
                  controls[index] = controls[index].concat("notNearKeywords");
                }
              }

              // Remove anything inside of the queries that are not accessible for this organization
              const cleanedTemplateQueries: UIQuery[] = templateQueries.map(
                (q) => ({
                  ...q,
                  sources: q.sources.filter((s) => sources.includes(s)),
                  languages: q.languages.filter((l) => languages.includes(l)),
                })
              );
              // fill options from values
              const queriesWithOptions = getQueriesWithOptions(
                cleanedTemplateQueries
              );

              // Reset all the states that might have been changed
              setOpenDropdown(null);
              setStartDate(undefined);
              setEndDate(undefined);
              setSortingPosition(10);
              setErrors({});
              setQueryErrors([]);
              setIsLoaded(false);
              setCreatedBy(null);
              setCreated(null);

              // preview loading and preview/save/refresh buttons
              setIsSaving(false);
              setPreviewButtonEnabled(false);
              setPreviewIsLoading(false);
              setSaveButtonEnabled(true);
              setPreviewErrors(undefined);
              setSearchDetailsModalVisible(false);
              setRefreshAlertModalOpen(false);

              // Update the query states
              setAddedControls(controls);
              setQueries(queriesWithOptions);
            }}
            warn={previewButtonEnabled}
          />
        </AccessControl>
      )}
      <div
        className={`querybuilder ${isCreateView ? "" : "pb-5 border-bottom"}`}
      >
        {!isCreateView && (
          <>
            <AccessControl level={ACCESS_LEVELS.ADMIN}>
              <CreatedBy createdBy={createdBy} created={created} />
            </AccessControl>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col col-span-3">
                <Label>{L.MENTIONS_QUERYBUILDER_TITLE_LABEL}</Label>
                <Input
                  block
                  type="text"
                  placeholder={L.MENTIONS_QUERYBUILDER_TITLE_PLACEHOLDER}
                  value={title}
                  onChange={(e) => setTitle(e.currentTarget.value)}
                  id="formGroupTitle"
                />
                {"title" in errors && (
                  <div className="invalid-feedback block">{errors.title}</div>
                )}
              </div>

              <div className="flex flex-col col-span-3">
                <Label>{L.MENTIONS_QUERYBUILDER_MODAL_TYPE_LABEL}</Label>
                <Select
                  block
                  className={`form-control ${"search_type" in errors ? "is-invalid" : ""}`}
                  onChange={(e) => setSearchType(e.target.value)}
                  value={searchType}
                  required
                  data-testid="search-type-select"
                  placeholder={L.MENTIONS_QUERYBUILDER_MODAL_TYPE_PLACEHOLDER}
                  options={Object.entries(optionTypeLabels).map(
                    ([value, label]) => ({
                      label: i18n._(label),
                      value,
                    })
                  )}
                />
                {"search_type" in errors && (
                  <div className="invalid-feedback block">
                    {L.MENTIONS_QUERYBUILDER_SEARCH_TYPE_ERROR}
                  </div>
                )}{" "}
              </div>

              <div
                className="flex flex-col"
                data-testid="start-date-form-group"
              >
                <Label>{L.MENTIONS_QUERYBUILDER_START_DATE_LABEL}</Label>
                <AESimpleDatepicker
                  className={`form-control ${"start_date" in errors ? "is-invalid" : ""}`}
                  dateFormat="yyyy-MM-dd"
                  onChange={(date) => setStartDate(date)}
                  popperPlacement="bottom-start"
                  selected={startDate}
                  wrapperClassName="flex"
                />
                {"start_date" in errors && (
                  <div className="invalid-feedback block">
                    {errors.start_date}
                  </div>
                )}
              </div>

              <div className="flex flex-col" data-testid="end-date-form-group">
                <Label>{L.MENTIONS_QUERYBUILDER_END_DATE_LABEL}</Label>
                <AESimpleDatepicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  dateFormat="yyyy-MM-dd"
                  className={`form-control ${"end_date" in errors ? "is-invalid" : ""}`}
                  popperPlacement="bottom-start"
                  wrapperClassName="flex"
                  minDate={startDate}
                  isClearable
                  clearButtonClassName="clear-button"
                  placeholderText={L.MENTIONS_QUERYBUILDER_END_DATE_PLACEHOLDER}
                />

                {"end_date" in errors && (
                  <div className="invalid-feedback block">
                    {errors.end_date}
                  </div>
                )}
              </div>

              <div
                className="flex flex-col"
                data-testid="sorting-position-form-group"
              >
                <div className="flex items-center gap-1">
                  <Label>
                    {L.MENTIONS_QUERYBUILDER_FILTER_SORTING_POSITION}{" "}
                  </Label>
                  <Tooltip
                    asChild={false}
                    content={
                      L.MENTIONS_QUERYBUILDER_FILTER_SORTING_POSITION_HELP
                    }
                  >
                    <InformationCircle width={14} />
                  </Tooltip>
                </div>

                <Input
                  block
                  type="number"
                  className={`form-control ${"sorting_position" in errors ? "is-invalid" : ""}`}
                  value={sortingPosition}
                  onChange={(e) => setSortingPosition(e.currentTarget.value)}
                  id="formGroupSortingPosition"
                />
                {"sorting_position" in errors && (
                  <div className="invalid-feedback block">
                    {errors.sorting_position}
                  </div>
                )}
              </div>

              {is_staff ? (
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <StaffBadge />
                    <Label>{L.MENTIONS_QUERYBUILDER_INDUSTRY_LABEL} </Label>
                    <Tooltip
                      content={L.MENTIONS_QUERYBUILDER_INDUSTRY_HELP}
                      asChild={false}
                    >
                      <InformationCircle width={14} />
                    </Tooltip>
                  </div>
                  <Select
                    block
                    nullable
                    className={`form-control ${"search_industry" in errors ? "is-invalid" : ""}`}
                    value={selectedIndustry || ""}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    data-testid="search-industry-select"
                    placeholder={L.MENTIONS_QUERYBUILDER_INDUSTRY_NONE}
                    options={selectableIndustries.map((si) => ({
                      value: si,
                      label: si,
                    }))}
                  />
                </div>
              ) : (
                ""
              )}
            </div>
          </>
        )}

        {uuid && !isLoaded && <Spinner className="mt-3 flex mx-auto" />}

        <div>
          {queries.map((query, index) => (
            <Query
              addQueryError={addQueryError}
              clearQueryError={clearQueryError}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              key={index}
              index={index}
              className="mt-4"
              tags={query.tags || []}
              keywords={query.keywords || []}
              languages={query.languages || []}
              sources={query.sources || []}
              nearKeywords={query.nearKeywords || []}
              nearNearKeywords={query.nearNearKeywords || []}
              notNearKeywords={query.notNearKeywords || []}
              nearDistance={query.nearDistance}
              notNearDistance={query.notNearDistance}
              includeChannelNames={query.includeChannelNames || []}
              excludeChannelNames={query.excludeChannelNames || []}
              minReachByChannelType={
                query.minReachByChannelType != null
                  ? query.minReachByChannelType
                  : { no_values: true }
              }
              excludeTopics={query.excludeTopics || []}
              mentionTypes={query.mentionTypes}
              includePaywalled={query.includePaywalled}
              // options
              options={query.options || {}}
              languageOptions={languages}
              sourceOptions={sources}
              removable={queries.length > 1}
              // handlers
              onRemoveKeyword={(keyword) =>
                removeKeywordFromQuery(keyword, "keywords", index)
              }
              onRemoveNearKeyword={(keyword) =>
                removeKeywordFromQuery(keyword, "nearKeywords", index)
              }
              onRemoveNearNearKeyword={(keyword) =>
                removeKeywordFromQuery(keyword, "nearNearKeywords", index)
              }
              onRemoveNotNearKeyword={(keyword) =>
                removeKeywordFromQuery(keyword, "notNearKeywords", index)
              }
              onAddQueryCopySettings={() => copySettingsQuery(index)}
              onRemove={() => {
                const q = queries?.filter((val, i) => i !== index) || [];
                setQueries(q);
              }}
              onAddValue={(name, val) => {
                // onAddValue adds values to arrays
                const q = [...queries];
                (q[index][name] as string[]) = (
                  (q[index][name] as string[]) || []
                ).concat([val]);
                setQueries(q);
                return q[index][name] as string[];
              }}
              onSetValue={(field, val) => {
                // onSetValue sets non-array values
                const q = [...queries];
                (q[index][field] as unknown) = val;
                setQueries(q);
              }}
              onSetMinReachByChannelType={(next) => {
                const q = [...queries];
                q[index].minReachByChannelType = next;
                setQueries(q);
              }}
              onRemoveValue={(name, val) => {
                const q = [...queries];
                (q[index][name] as string[]) = (
                  (q[index][name] as string[]) || []
                ).filter((v: unknown) => v !== val);
                setQueries(q);
                return q[index][name] as string[];
              }}
              canCreateValue={(val, name) => {
                if (!query.options) query.options = {};
                if (!query.options[name]) query.options[name] = [];
                if (!val || !val.trim()) return false;
                if (query.options[name]?.includes(val)) return false;
                return true;
              }}
              onCreateValue={(name: keyof UIQuery, val) => {
                // We don't want any whitespace before or after keyword
                const value = val.trim();

                if (!value) return false;
                const q = [...queries];

                // create option
                if (!query.options) query.options = {};
                if (!query.options[name]) query.options[name] = [];

                // dont add same values multiple times
                if (query.options[name].includes(value)) {
                  return false;
                }

                query.options[name] = query.options[name].concat([value]);

                // add value
                (query[name] as string[]) = (
                  (query[name] as string[]) || []
                ).concat([value]);
                q[index] = query;

                setQueries(q);
                return true;
              }}
              onKeywordChange={(values: string[], fieldName) => {
                const q = [...queries];
                (q[index][fieldName] as unknown) = values;
                if (q[index].options) {
                  q[index].options[fieldName] = values;
                }
                setQueries(q);
              }}
              addedControls={addedControls[index]}
              onAddControl={(name) => {
                const added = [...addedControls];
                added[index] = (added[index] || []).concat([name]);
                setAddedControls(added);
                setOpenDropdown({ filterName: name, index });

                const q = [...queries];
                if (name === "nearKeywords") {
                  q[index]["nearDistance"] = 10;
                } else if (name === "notNearKeywords") {
                  q[index]["notNearDistance"] = 10;
                } else if (name === "mentionTypes") {
                  q[index]["mentionTypes"] = "speech";
                } else if (name == "includePaywalled") {
                  q[index]["includePaywalled"] = true;
                } else if (name === "minReachByChannelType") {
                  // Values already there
                } else {
                  q[index][name] = undefined;
                }
                setQueries(q);
              }}
              onRemoveControl={(name) => {
                const added = [...addedControls];
                added[index] = added[index].filter((c) => c !== name);
                setAddedControls(added);

                const q = [...queries];
                if (name === "nearKeywords") {
                  q[index]["nearDistance"] = 10;
                } else if (name === "notNearKeywords") {
                  q[index]["notNearDistance"] = 10;
                } else if (name === "mentionTypes") {
                  q[index]["mentionTypes"] = "all";
                } else if (name == "includePaywalled") {
                  q[index]["includePaywalled"] = false;
                } else {
                  q[index][name] = undefined;
                }

                setQueries(q);
              }}
            />
          ))}
          {errors?.queries && (
            <div className="invalid-feedback block">
              {errors.queries.map((e, queryIndex) =>
                Object.keys(e).map((subError) => (
                  <div key={`query-error-${queryIndex}`}>
                    {Array.isArray(e[subError]) ? (
                      e[subError].map((fieldError, index) => (
                        <div key={`${subError}-${queryIndex}-${index}`}>
                          <span>{fieldError}</span>
                          <br />
                        </div>
                      ))
                    ) : (
                      <div key={`${subError}-${queryIndex}`}>
                        <span>{e[subError]}</span>
                        <br />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
          {errors?.non_field_errors && (
            <div className="invalid-feedback block">
              {errors.non_field_errors.map((e, index) => {
                return (
                  <div key={`query-error-${index}`}>
                    <span>{e}</span>
                    <br />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="flex justify-end">
            <Button
              variant="secondary"
              onClick={onAddQuery}
              icon={PlusIcon}
              disabled={!isCreateView && !isLoaded}
            >
              {L.MENTIONS_QUERYBUILDER_ADD_MORE_CLAUSES}
            </Button>
          </div>
        </div>

        <div className="mt-5">
          {!is_readonly && !isCreateView ? (
            <Button
              variant="tertiary"
              onClick={refreshAlert}
              icon={ReloadIcon}
              data-testid="reset-mention-button"
            >
              {L.MENTION_SETTING_REFRESH_ALERT}
            </Button>
          ) : null}
          <div className="flex justify-end">
            {isCreateView ? (
              <>
                <Button
                  className="ml-2"
                  id="query-builder-preview"
                  disabled={
                    previewIsLoading ||
                    !previewButtonEnabled ||
                    hasQueryErrors()
                  }
                  onClick={showPreview}
                  size="lg"
                  variant="tertiary"
                >
                  {L.MENTIONS_QUERYBUILDER_PREVIEW_BUTTON}
                </Button>

                <Button
                  className="ml-2"
                  disabled={isSaving || !saveButtonEnabled || hasQueryErrors()}
                  id="query-builder-save"
                  data-testid="query-builder-save"
                  onClick={setTitleAndDetails}
                  size="lg"
                  variant="primary"
                >
                  {L.MENTIONS_QUERYBUILDER_CONTINUE_BUTTON}
                </Button>
              </>
            ) : (
              <AccessControl visible>
                <Button
                  className="ml-2"
                  disabled={isSaving || !saveButtonEnabled || hasQueryErrors()}
                  id="query-builder-save"
                  data-testid="query-builder-save"
                  onClick={saveSearch}
                  size="lg"
                  variant="primary"
                >
                  {L.MENTIONS_QUERYBUILDER_UPDATE_BUTTON}
                </Button>
              </AccessControl>
            )}
          </div>
        </div>
        {!isCreateView && (
          <div className="pt-2">
            {!is_readonly && (
              <div>
                <Typography align="left" variant="caption">
                  {L.MENTION_SETTING_REFRESH_ALERT_DISCLAIMER}
                </Typography>
              </div>
            )}
            <div className="flex justify-end">
              <Typography align="right" variant="caption">
                {L.MENTIONS_QUERYBUILDER_UPDATE_DISCLAIMER}
              </Typography>
            </div>
          </div>
        )}

        {previewIsLoading && (
          <div className="flex justify-center my-3">
            <Spinner />
          </div>
        )}

        {previewErrors && (
          <div>
            <div>
              <div className="border-top pt-4 my-4">
                <div className="alert alert-danger mt-4">{previewErrors}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      <DetailsModal
        show={searchDetailsModalVisible}
        onHide={hideSearchDetailsModal}
        title={title}
        setTitle={setTitle}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        errors={errors}
        sortingPosition={sortingPosition}
        setSortingPosition={setSortingPosition}
        onClose={onDetailsModalClose}
        isSaving={isSaving}
        onSave={saveSearch}
        searchType={searchType}
        setSearchType={setSearchType}
        optionTypeLabels={optionTypeLabels}
      />
      <ConfirmModal
        onCancel={closeRefreshAlertModal}
        show={refreshAlertModalOpen}
        onConfirm={confirmRefreshAlert}
        title={L.MENTIONS_VIEW_RESET_MENTIONS}
        message={getConfirmRefreshAlertMessage()}
      />
    </>
  );
};

export default QueryBuilder;
